import { z } from 'zod';

export const emptyRule = <T>(
  s: z.ZodType<T>,
  t: 'string' | 'number' | 'boolean' = 'string',
  defaultValue?: T,
) =>
  z.preprocess((v) => {
    if (v === null || v === undefined || typeof v !== t) {
      if (typeof defaultValue !== 'undefined') return defaultValue;
      return t === 'string' ? '' : t === 'number' ? 0 : false;
    }
    return v;
  }, s) as z.ZodType<T, z.ZodTypeDef, T>;

export const requiredString = (name: string, maxLen?: number) => {
  let schema = z.string().min(1, { message: `${name} is required` });
  if (maxLen) schema = schema.max(maxLen, { message: `${name} is too long` });
  return emptyRule(schema, 'string');
};

export const requiredNumber = (name: string, min?: number, max?: number) => {
  let schema = z.number().min(min ?? 1, { message: `${name} is required` });
  if (typeof min !== 'undefined')
    schema = schema.min(min, {
      message: `${name} has to be bigger than ${min}`,
    });
  if (typeof max !== 'undefined')
    schema = schema.max(max, {
      message: `${name} has to be smaller than ${max}`,
    });
  return emptyRule(schema, 'number', min);
};
export const optionalNumber = () => z.number().nullable().optional();
export const optionalBoolean = () => z.boolean().optional().nullable();
export const optionalString = () => emptyRule(z.string());

export const optionalArray = <T extends z.ZodTypeAny>(v: T) =>
  z.array(v).optional().default([]);

export const optionalObjectArray = <T extends z.ZodRawShape>(v: T) =>
  optionalArray(z.object(v));

export const optionalObject = <T extends z.ZodRawShape>(v: T) =>
  z.object(v).optional();

export const requiredUnion = <
  T extends readonly [z.ZodTypeAny, z.ZodTypeAny, ...z.ZodTypeAny[]],
>(
  types: T,
  name: string,
) =>
  z
    .union(types)
    .or(z.undefined())
    .refine((value) => value !== undefined, {
      message: `${name} is required`,
    });
